'use client';

import { ReactNode, useState, Children, isValidElement } from 'react';
import { ChevronDown, Plus } from 'lucide-react';

interface AccordionItemProps {
  title: ReactNode;
  children: ReactNode;
}

export function AccordionItem(_props: AccordionItemProps) {
  // This is just a marker component - actual rendering happens in Accordion
  return null;
}

interface HoverInsertZoneProps {
  onInsert: () => void;
}

function HoverInsertZone({ onInsert }: HoverInsertZoneProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative -my-1.5"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 -mt-[3px] h-8 z-10" />
      {isHovered && (
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 -mt-[3px] flex items-center justify-center z-20 animate-in fade-in duration-150">
          <div className="flex-1 h-0.5 bg-blue-400 dark:bg-blue-500" />
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onInsert();
            }}
            className="mx-2 p-1.5 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-full shadow-lg transition-all hover:scale-110"
            title="Vložit úkol zde"
          >
            <Plus className="w-4 h-4" />
          </button>
          <div className="flex-1 h-0.5 bg-blue-400 dark:bg-blue-500" />
        </div>
      )}
    </div>
  );
}

interface AccordionProps {
  children: ReactNode;
  allowMultiple?: boolean;
  defaultOpenItems?: number[];
  draggable?: boolean;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  showInsertZones?: boolean;
  onInsertAt?: (index: number) => void;
}

export function Accordion({ children, allowMultiple = true, defaultOpenItems = [], draggable = false, onReorder, showInsertZones = false, onInsertAt }: AccordionProps) {
  const [openItems, setOpenItems] = useState<number[]>(defaultOpenItems);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const items = Children.toArray(children)
    .filter(isValidElement)
    .map((child) => ({
      title: (child.props as AccordionItemProps).title,
      content: (child.props as AccordionItemProps).children,
    }));

  const toggleItem = (index: number) => {
    if (allowMultiple) {
      setOpenItems((prev) =>
        prev.includes(index)
          ? prev.filter((i) => i !== index)
          : [...prev, index]
      );
    } else {
      setOpenItems((prev) => (prev.includes(index) ? [] : [index]));
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex && onReorder) {
      onReorder(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="my-8 space-y-3">
      {items.map((item, index) => {
        const isOpen = openItems.includes(index);
        const isDragging = draggedIndex === index;
        const isDragOver = dragOverIndex === index;

        return (
          <div key={index}>
            {showInsertZones && onInsertAt && (
              <HoverInsertZone onInsert={() => onInsertAt(index)} />
            )}

            <div
              draggable={draggable && !isOpen}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`rounded-lg border-2 bg-white dark:bg-gray-700 overflow-hidden transition-all ${
                isDragging
                  ? 'opacity-50 border-indigo-500 dark:border-indigo-400'
                  : isDragOver
                  ? 'border-indigo-400 dark:border-indigo-500 scale-[1.02]'
                  : 'border-gray-200 dark:border-gray-500'
              } ${draggable && !isOpen ? 'cursor-move' : ''}`}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleItem(index);
                }}
                className={`w-full flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors ${
                  isOpen ? 'p-4 pb-2' : 'p-4'
                }`}
              >
                <span className="font-medium text-gray-900 dark:text-gray-100">{item.title}</span>
                <ChevronDown
                  className={`h-5 w-5 text-gray-600 dark:text-gray-300 transition-transform ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isOpen && (
                <div className="px-4 pb-4 pt-2 text-sm text-gray-600 dark:text-gray-300">
                  {item.content}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {showInsertZones && onInsertAt && items.length > 0 && (
        <HoverInsertZone onInsert={() => onInsertAt(items.length)} />
      )}
    </div>
  );
}
